/**
 * Home hero media readiness helpers for the app splash loader.
 * Waits until the mounted hero video can play (or image has loaded).
 */

export const isHomePath = (pathname = "") => {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  // /en | /ar | /ku  (language root = home)
  if (parts.length === 1) {
    const lang = parts[0].toLowerCase().split("-")[0];
    return lang === "en" || lang === "ar" || lang === "ku";
  }
  return false;
};

const HOME_HERO_WAIT_MS = 20000;

const waitForElement = (selector, timeoutMs) =>
  new Promise((resolve) => {
    const existing = document.querySelector(selector);
    if (existing) {
      resolve(existing);
      return;
    }

    const started = Date.now();
    const timer = setInterval(() => {
      const el = document.querySelector(selector);
      if (el) {
        clearInterval(timer);
        resolve(el);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        clearInterval(timer);
        resolve(null);
      }
    }, 50);
  });

const waitForVideoReady = (video) =>
  new Promise((resolve) => {
    if (!video) {
      resolve();
      return;
    }

    // HAVE_FUTURE_DATA (3) / HAVE_ENOUGH_DATA (4) — enough to play smoothly
    if (video.readyState >= 3) {
      resolve();
      return;
    }

    const done = () => {
      video.removeEventListener("canplaythrough", done);
      video.removeEventListener("loadeddata", done);
      video.removeEventListener("error", done);
      resolve();
    };

    video.addEventListener("canplaythrough", done, { once: true });
    video.addEventListener("loadeddata", done, { once: true });
    video.addEventListener("error", done, { once: true });
  });

const waitForImageReady = (img) =>
  new Promise((resolve) => {
    if (!img) {
      resolve();
      return;
    }
    if (img.complete && img.naturalWidth > 0) {
      resolve();
      return;
    }
    const done = () => {
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      resolve();
    };
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });

/**
 * Resolves when the home hero background video/image is mounted and ready,
 * or when the timeout elapses (so the splash never hangs forever).
 */
export const waitForHomeHeroMedia = (timeoutMs = HOME_HERO_WAIT_MS) =>
  Promise.race([
    (async () => {
      const video = await waitForElement(".hero-bgVideo", timeoutMs);
      if (video) {
        await waitForVideoReady(video);
        return;
      }
      const image = await waitForElement(".hero-bgImage", timeoutMs);
      if (image) {
        await waitForImageReady(image);
      }
    })(),
    new Promise((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
